import { type FC, type ReactNode, useEffect, useRef } from "react";

import { clsx } from "clsx";

import { Checkbox } from "./checkbox";
import { LinkButton } from "./link_button";
import { SectionLabel } from "./section_label";
import { Skeleton } from "./skeleton";

// Easy setup's review in the left pane: what it found in his template, its
// groups, and the actions under them.
export const SetupReviewCard: FC<{
  title: ReactNode;
  lead: ReactNode;
  children: ReactNode;
  footer: ReactNode;
}> = ({ title, lead, children, footer }) => (
  <section aria-label="Set up your sections" className="space-y-3">
    <div>
      <h3 className="text-[13px] font-semibold text-slate-800">{title}</h3>
      <p className="text-[11.5px] text-slate-500">{lead}</p>
    </div>
    {children}
    <div className="space-y-2 border-t border-slate-200 pt-3">{footer}</div>
  </section>
);

// A group of the review, inset as the Sections panel is.
export const SetupReviewGroup: FC<{
  label: string;
  line: string;
  children: ReactNode;
}> = ({ label, line, children }) => (
  <div className="space-y-1 rounded-[10px] bg-slate-100 p-2">
    <div className="flex px-1">
      <SectionLabel size="xs">
        {label} <span className="font-normal normal-case">· {line}</span>
      </SectionLabel>
    </div>
    {children}
  </div>
);

// The label over a top heading's sub-sections.
export const SetupReviewParent: FC<{ children: ReactNode }> = ({
  children,
}) => (
  <div className="flex px-1 pt-1">
    <SectionLabel size="xs" tone="muted">
      {children}
    </SectionLabel>
  </div>
);

// A line of the review with nothing to choose: a section gone from his
// template, or one whose heading he reworded.
export const SetupReviewNote: FC<{ children: ReactNode }> = ({ children }) => (
  <p className="rounded-md bg-white px-2 py-1.5 text-[12.5px] text-slate-500">
    {children}
  </p>
);

// A fill-in field: his label, then its value (`children`) and, after it, the
// grey line that says where it's from. One line, the same height editing.
export const SetupReviewField: FC<{
  label: string;
  children: ReactNode;
  line?: string | null;
}> = ({ label, children, line }) => (
  <div className="flex h-8 items-center gap-2 rounded-md bg-white px-2 text-[12.5px] leading-5">
    <span className="w-2/5 shrink-0 truncate text-slate-500">{label}</span>
    <span className="flex min-w-0 flex-1 items-center text-slate-700">
      {children}
      {line && (
        <span className="shrink-0 text-[11px] text-slate-400">
          &nbsp;· {line}
        </span>
      )}
    </span>
  </div>
);

// One proposed section: kept or not, its name (editable), the grey line under
// it, and what it reads on the right. A sub-section sits indented. Selected
// on a click anywhere in it, once anything in it has focus, or when its place
// in the Report is clicked, and then outlined and scrolled into view.
export const SetupReviewRow: FC<{
  label: string;
  checked: boolean;
  onCheck(): void;
  selected: boolean;
  onSelect(): void;
  name: ReactNode;
  line?: string | null;
  reads: ReactNode;
  nested: boolean;
}> = ({
  label,
  checked,
  onCheck,
  selected,
  onSelect,
  name,
  line,
  reads,
  nested,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (selected) ref.current?.scrollIntoView({ block: "nearest" });
  }, [selected]);

  return (
    <div
      ref={ref}
      aria-current={selected || undefined}
      onFocus={onSelect}
      onClick={onSelect}
      className={clsx(
        "flex scroll-my-4 items-start gap-2 rounded-md bg-white px-2 py-1.5",
        nested && "ml-3",
        !checked && "opacity-60",
        selected && "ring-2 ring-violet-400",
      )}
    >
      <Checkbox
        className="mt-1"
        checked={checked}
        onChange={onCheck}
        aria-label={`Add ${label}`}
      />
      <div className="min-w-0 flex-1">
        {name}
        {line && <p className="text-[11px] text-slate-400">{line}</p>}
      </div>
      <div className="shrink-0">{reads}</div>
    </div>
  );
};

// A row's name, edited in place: its row's outline shows it's picked, and a
// faint underline that it's being typed in.
export const SetupReviewName: FC<{
  value: string;
  onChange(value: string): void;
}> = ({ value, onChange }) => (
  <input
    aria-label="Section name"
    value={value}
    onChange={(event) => onChange(event.target.value)}
    className="w-full rounded border-0 bg-transparent p-0 text-[12.5px] text-slate-700 decoration-slate-300 underline-offset-2 focus:underline focus:shadow-none focus:ring-0 focus:outline-none"
  />
);

// Add, and beside it the way to set them up himself, when there is one.
export const SetupReviewActions: FC<{
  add: ReactNode;
  skipLabel?: string;
  onSkip?(): void;
}> = ({ add, skipLabel, onSkip }) => (
  <div className="flex items-center gap-4">
    {add}
    {/* Its own box, so it centers on the button rather than taking the top. */}
    {onSkip && (
      <span>
        <LinkButton weight="medium" onClick={onSkip}>
          {skipLabel}
        </LinkButton>
      </span>
    )}
  </div>
);

// Rows still loading.
export const SetupReviewSkeleton: FC = () => (
  <div className="space-y-1.5">
    {[0, 1, 2, 3, 4].map((row) => (
      <Skeleton key={row} className="h-7 rounded-md bg-slate-100" />
    ))}
  </div>
);
